import React, { useMemo } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { Skeleton } from "antd";
import { useCallback, useEffect, useState } from "react";
import { Button } from "../../../common/Button";
import { useStack } from "../../../hooks/useStaking";
import useWindowSize from "../../../hooks/useWindowSize";
import NftCard from "../NftCard";
import {
  ClaimButton,
  CollectionMain,
  CollectionRoot,
  ConnectInfo,
  GridContainer,
  TabButtons,
  LinkButton,
  AlertMessage,
  ConnectWalletDescription,
} from "./styles";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime"; // import plugin
import "dayjs/locale/en";
import UpdateModal from "../../../common/UpdateModal";

dayjs.extend(relativeTime); // use plugin
dayjs.locale("en"); // use locale

const Collections = () => {
  const {
    animals,
    stakedAnimals,
    claimAllStakingRewards,
    animalsStatus,
    stakedAnimalsStatus,
    avaliableStakedAnimals,
    setAvaliableStakedAnimals,
  } = useStack();
  const [active, setActive] = useState(1);
  const wallet = useWallet();
  const [animalsReady, setAnimalsReady] = useState(false);
  const [stakedAnimalsReady, setStakedAnimalsReady] = useState(false);
  const [opened, setOpened] = useState(true);
  const [isClaimAllDisabled, setIsClaimAllDisabled] = useState(true);
  const { isMobile } = useWindowSize();

  useEffect(() => {
    const getAvaliableStakedAnimals = async () => {
      await setAvaliableStakedAnimals();
    };
    if (stakedAnimals.length > 0) getAvaliableStakedAnimals();
  }, [stakedAnimals]);

  useEffect(() => {
    const interval = setInterval(() => {
      const res = avaliableStakedAnimals.filter((animal) => {
        const endDate = dayjs(animal?.lastClaim).add(3, "days");
        if (animal && dayjs(endDate).diff(dayjs()) <= 0) {
          return true;
        } else return false;
      });
      setIsClaimAllDisabled(res.length === 0);
    }, 500);
    return () => clearInterval(interval);
  }, [avaliableStakedAnimals]);

  const anchorWallet = useMemo(() => {
    setActive(1);
    localStorage.setItem("active", "1");
    if (
      !wallet ||
      !wallet.publicKey ||
      !wallet.signAllTransactions ||
      !wallet.signTransaction
    ) {
      return;
    }
    return {
      publicKey: wallet.publicKey,
      signAllTransactions: wallet.signAllTransactions,
      signTransaction: wallet.signTransaction,
    };
  }, [wallet]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      //@ts-ignore
      const isActive = JSON.parse(localStorage.getItem("active"));
      if (isActive) {
        setActive(Number(isActive));
      } else {
        localStorage.setItem("active", active.toString());
      }
    }
  }, [typeof window, active]);

  const handleActiveTab = (key: number) => {
    let tabToSet = key;
    if (
      key === 2 &&
      animalsReady &&
      !animals.length &&
      animalsStatus.loadingEnd &&
      !stakedAnimals.length
    )
      tabToSet = 1;
    setActive(tabToSet);
    localStorage.setItem("active", tabToSet.toString());
  };

  const handleClaimAll = useCallback(async () => {
    try {
      await claimAllStakingRewards();
    } catch (e) {
      console.log(e);
    }
  }, [claimAllStakingRewards]);

  // loading state and check has animals
  useEffect(() => {
    setAnimalsReady(false);
    if (animals.length) {
      setAnimalsReady(false);
      return;
    }
    if (!animals.length && !animalsReady) {
      const timeoutID = window.setTimeout(() => {
        setAnimalsReady(true);
      }, 1000);
      return () => window.clearTimeout(timeoutID);
    }
  }, [animals.length]);

  useEffect(() => {
    if (animalsReady && animals.length) {
      setAnimalsReady(false);
    }
  }, [animalsReady, animals]);

  // loading state and check has staked animals
  useEffect(() => {
    setStakedAnimalsReady(false);

    if (stakedAnimals.length) {
      setStakedAnimalsReady(false);
      return;
    }
    if (!stakedAnimals.length && !stakedAnimalsReady) {
      const timeoutID = window.setTimeout(() => {
        setStakedAnimalsReady(true);
      }, 1000);
      return () => window.clearTimeout(timeoutID);
    }
  }, [stakedAnimals.length]);

  useEffect(() => {
    if (stakedAnimalsReady && stakedAnimals.length) {
      setStakedAnimalsReady(false);
    }
  }, [stakedAnimalsReady, stakedAnimals]);

  return (
    <>
      {/* <UpdateModal modalIsOpen={opened} setIsOpen={setOpened} /> */}
      {wallet.connected && anchorWallet?.publicKey ? (
        <CollectionRoot>
          {/* Nft collection Tab */}
          <TabButtons>
            <Button
              color={active === 1 ? "primary" : ""}
              onClick={() => handleActiveTab(1)}
            >
              My NFT's
            </Button>
            <Button
              color={active === 2 ? "primary" : ""}
              onClick={() => handleActiveTab(2)}
            >
              Staked NFT's
            </Button>
          </TabButtons>

          <CollectionMain>
            <div
              style={{
                display: active === 1 ? "flex" : "none",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "50vh",
                marginTop: "-40px",
                paddingTop: isMobile ? "" : "45px",
              }}
            >
              {animalsReady && !animals.length && animalsStatus.loadingEnd ? (
                <>
                  {stakedAnimals.length ? (
                    <AlertMessage>
                      <h3>All NFT's are staked get some more?</h3>
                      <LinkButton
                        href="https://www.magiceden.io/marketplace/baby_ape_social_club"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Click Here
                      </LinkButton>
                    </AlertMessage>
                  ) : (
                    <AlertMessage>
                      <h3>You have no BASC NFTs :(</h3>
                      <LinkButton
                        href="https://www.magiceden.io/marketplace/baby_ape_social_club"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Buy Now
                      </LinkButton>
                    </AlertMessage>
                  )}
                </>
              ) : (
                <GridContainer>
                  {animalsStatus.loading
                    ? [...Array(3)].map((arr, i) => (
                        <Skeleton.Avatar
                          key={i}
                          active
                          shape="square"
                          size={300}
                        />
                      ))
                    : animals?.map((dolphin) => (
                        <NftCard
                          key={dolphin.uriData.image}
                          isStaked={false}
                          token={dolphin}
                        />
                      ))}
                </GridContainer>
              )}
            </div>

            {/* staked NFTS */}
            <div
              style={{
                display: active === 2 ? "flex" : "none",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                minHeight: "50vh",
                marginTop: "-40px",
              }}
            >
              {!!stakedAnimals.length && stakedAnimals.length < 10 && (
                <ClaimButton isDisabled={false} onClick={handleClaimAll}>
                  Claim All Rewards
                </ClaimButton>
              )}
              {stakedAnimalsReady &&
              !stakedAnimals.length &&
              stakedAnimalsStatus.loadingEnd ? (
                <>
                  {!stakedAnimals.length && (
                    <AlertMessage>
                      <h3>No NFT's are Staked... yet {`;)`}</h3>
                    </AlertMessage>
                  )}
                </>
              ) : (
                <>
                  <GridContainer>
                    {avaliableStakedAnimals.length
                      ? avaliableStakedAnimals?.map((dolphin) => (
                          <NftCard
                            key={dolphin.uriData.image}
                            isStaked={true}
                            token={dolphin}
                          />
                        ))
                      : stakedAnimalsStatus.loading
                      ? [...Array(3)].map((arr, i) => (
                          <Skeleton.Avatar
                            key={i}
                            active
                            shape="square"
                            size={300}
                          />
                        ))
                      : ""}
                  </GridContainer>
                  {!stakedAnimals.length && !stakedAnimalsStatus.loading && (
                    <h2 className="info">No NFT's are Staked... yet {`;)`}</h2>
                  )}
                </>
              )}
            </div>
          </CollectionMain>
        </CollectionRoot>
      ) : (
        <ConnectInfo>
          <AlertMessage>
            <ConnectWalletDescription>
              Connect wallet for staking!
            </ConnectWalletDescription>
          </AlertMessage>
        </ConnectInfo>
      )}
    </>
  );
};

export default Collections;
